import { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'

type UsePersistedHashValueOptions<T extends string> = {
  defaultValue: T
  storageKey?: string
  isValid: (value: string) => value is T
  hashKey?: string
  persistInLocalStorage?: boolean
}

const usePersistedHashValue = <T extends string>({
  defaultValue,
  storageKey,
  isValid,
  hashKey,
  persistInLocalStorage = true,
}: UsePersistedHashValueOptions<T>) => {
  const { hash } = useLocation()
  const [value, setValueState] = useState<T>(() => {
    const fromHash = hashKey
      ? new URLSearchParams(hash?.replace('#', '')).get(hashKey) ?? undefined
      : hash?.replace('#', '')
    if (fromHash && isValid(fromHash)) {
      return fromHash
    }

    if (persistInLocalStorage && storageKey) {
      const stored = localStorage.getItem(storageKey)
      if (stored && isValid(stored)) {
        return stored
      }
    }

    return defaultValue
  })

  const setValue = useCallback(
    (nextValue: T) => {
      setValueState(nextValue)
      if (persistInLocalStorage && storageKey) {
        localStorage.setItem(storageKey, nextValue)
      }
      if (!hashKey) {
        history.replaceState({}, '', `#${nextValue}`)
        return
      }
      const hashParams = new URLSearchParams(location.hash.replace('#', ''))
      hashParams.set(hashKey, nextValue)
      history.replaceState({}, '', `#${hashParams.toString()}`)
    },
    [hashKey, persistInLocalStorage, storageKey],
  )

  return { value, setValue }
}

export default usePersistedHashValue
